import { Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client";
import { jwtDecode } from "jwt-decode";
import { httpService } from "./HttpService";
import { storageService } from "./StorageService";

class AuthService {
    private userManagerSettings: UserManagerSettings = {
        authority: process.env.REACT_APP_AUTH_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL,
        client_id: process.env.REACT_APP_CLIENT_ID,
        response_type: "code",
        response_mode: "query",
        post_logout_redirect_uri: `${window.location.origin}/logout`,
        redirect_uri: `${window.location.origin}/auth-result`,
        scope: "IdentityServerApi TokenserverAPI.write TokenserverAPI.read Tenn.CRM offline_access Tenn.FileStorage Tenn.SFTP Tenn.Payment Tenn.RiskService Tenn.Messaging Tenn.Issuing",
        automaticSilentRenew: false,
        silent_redirect_uri: `${window.location.origin}/silentRenew`,
        accessTokenExpiringNotificationTime: 300,
        checkSessionInterval: 1000,
        silentRequestTimeout: 1000,
        stateStore: new WebStorageStateStore({ store: window.sessionStorage })
    };

    // https://medium.com/@franciscopa91/how-to-implement-oidc-authentication-with-react-context-api-and-react-router-205e13f2d49
    private userManager: UserManager;

    constructor() {
        this.userManager = new UserManager(this.userManagerSettings);

        this.userManager.events.addSilentRenewError((error: Error) => {
            console.log("Silent Renew failed with Error", error);
        });

        this.userManager.events.addAccessTokenExpired(() => {
            console.log("Access Token Expired");
            this.signinSilent();
        });

        // Logger
        Log.logger = console;
        Log.level = Log.DEBUG;
    }

    getUser = async () : Promise<User | null> => {
        try {
            return  this.userManager.getUser();
        } catch (error) {
            return null;
        }
    };

    signinRedirect = async () : Promise<void> => {
        await this.userManager.signinRedirect();
    };

    /**
     * Whether this browser holds a session that has not expired yet.
     *
     * The presence of a token is not a session. Checking only for existence is what let an
     * operator keep browsing on a dead session - and be offered a Logout button that could not
     * work - which is half of TNPDEVAPPS-1868.
     */
    isAuthenticated = () : boolean => {
        const storedUser = this.readStoredUser();

        // expires_at is seconds since the epoch.
        if (storedUser?.expires_at) {
            return storedUser.expires_at * 1000 > Date.now();
        }

        // A second tab has its own sessionStorage and so no stored oidc user, but it shares
        // localStorage with the tab that signed in. Fall back to the token the app actually
        // sends, so opening the portal in a new tab is not mistaken for being signed out.
        return this.isStoredAccessTokenValid();
    };

    signinRedirectCallback = async () : Promise<User> => {
        return this.userManager.signinRedirectCallback();
    };

    signinSilentCallback = async () : Promise<void> => {
        try {
            const user = await this.userManager.signinSilentCallback();
            const token = `${user?.token_type} ${user?.access_token}`;

            storageService.setAccessToken(token);
            httpService.setAuthToken(token);
        } catch (error) {
            console.log("signinSilentCallback failed with Error", error);
        }
    };

    signoutRedirect = async () : Promise<void> => {
        await this.userManager.clearStaleState();

        // Do NOT clear localStorage/sessionStorage here. The UserManager reads the
        // stored oidc user to attach id_token_hint to the end-session request. If we
        // wipe it first, the request goes out without id_token_hint, and once the
        // server session has also expired the auth server cannot resolve
        // post_logout_redirect_uri — it falls back to a 404 home page ("This page
        // cannot be displayed") instead of returning the user to Login. Storage is
        // cleared in the callback below, after the round-trip has been built.
        try {
            return await this.userManager.signoutRedirect();
        } catch (error) {
            // ...but a logout that never reaches the auth server must still end the session
            // in this browser. Leaving storage intact on failure is what let an operator see
            // a logout error, stay signed in, and carry on reading data (TNPDEVAPPS-1868).
            // The round trip has already been attempted by this point, so clearing now cannot
            // cost us the id_token_hint.
            console.error("signoutRedirect failed; clearing the local session anyway", error);
            this.clearLocalSession();

            throw error;
        }
    };

    signoutRedirectCallback = async () : Promise<void> => {
        try {
            await this.userManager.signoutRedirectCallback();
        } catch (error) {
            // The server has signed us out by the time it redirects here; a callback that
            // cannot be parsed must not leave the browser holding a usable session.
            console.error("signoutRedirectCallback failed; clearing the local session anyway", error);
        } finally {
            this.clearLocalSession();
        }
    };

    signinSilent = async () => {
        try {
            const user = await this.userManager.signinSilent();
            const token = `${user.token_type} ${user.access_token}`;

            storageService.setAccessToken(token);
            httpService.setAuthToken(token);
        } catch (error) {
            console.log("signinSilent failed", error);
            throw error;
        }
    };

    /** Everything that marks this browser as signed in, dropped as one unit. */
    private clearLocalSession = () => {
        try {
            localStorage.clear();
        } catch (error) {
            console.error("Could not clear localStorage", error);
        }

        try {
            sessionStorage.clear();
        } catch (error) {
            console.error("Could not clear sessionStorage", error);
        }
    };

    /** The oidc user as the UserManager stored it, or null if it is absent or unreadable. */
    private readStoredUser = () : User | null => {
        const key = `oidc.user:${process.env.REACT_APP_AUTH_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL}:${process.env.REACT_APP_CLIENT_ID}`;

        try {
            const stored = sessionStorage.getItem(key);

            return stored ? (JSON.parse(stored) as User) : null;
        } catch (error) {
            // JSON.parse on a missing or half-written entry throws; that is "no session",
            // not an error worth propagating to a caller asking a yes/no question.
            return null;
        }
    };

    private isStoredAccessTokenValid = () : boolean => {
        const token = storageService.getAccessToken().split(" ")[1];

        if (!token) {
            return false;
        }

        try {
            const { exp } = jwtDecode(token);

            return !!exp && exp * 1000 > Date.now();
        } catch (error) {
            return false;
        }
    };
}

const authService = new AuthService();

export { authService };
